<template>
    <div id="ListComment">
        <div v-for="comment in comments" :key="comment._id" class="comment-item">
            <div class="comment-header">
                <Avatar
                    v-if="usersMap[comment.user]"
                    :size="35"
                    :src="usersMap[comment.user].gravatar"
                    :username="usersMap[comment.user].name"
                />
                <span class="comment-username">
                    {{ usersMap[comment.user] ? usersMap[comment.user].name : "Unknown User" }}
                    <span v-if="comment.edited" class="edited-label">{{ $t("task.edited") }}</span>
                </span>
                <span class="comment-date">{{ formatDate(comment.createdAt) }}</span>
            </div>
            <div class="comment-body">
                <div v-if="editingCommentId === comment._id">
                    <textarea v-model="editedCommentMessage"></textarea>
                    <button @click="cancelEdit">{{ $t("calendar.cancel") }}</button>
                    <button @click="saveComment(comment._id)">{{ $t("calendar.save") }}</button>
                </div>
                <div v-else>
                    {{ comment.message }}
                </div>
                <div class="comment-actions" v-if="editingCommentId !== comment._id">
                    <a @click="editComment(comment)" v-if="comment.user === user._id">{{ $t("chatbotv2.editBlock")
                        }}</a>
                    
                    <a @click="confirmDeleteComment(comment._id)">{{ $t("chatbotv2.delete") }}</a>
                </div>
            </div>
        </div>
        <div v-if="showDeleteConfirm" class="delete-confirm-overlay" @click="cancelDelete">
            <div class="delete-confirm-dialog" @click.stop>
                <div class="confirm-content">
                    <svg class="alert-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12" y2="16"></line>
                    </svg>
                    <p>{{ $t("task.deleteComment") }}</p>
                </div>
                <button @click="deleteComment(deleteCommentId)">{{ $t("message.yes") }}</button>
                <button @click="cancelDelete">{{ $t("message.no") }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Avatar from "vue-avatar";
import moment from "moment";

export default {
    name: "ListComment",
    props: {
        taskId: {
            type: String,
            required: true,
        },
    },
    components: {
        Avatar,
    },
    data() {
        return {
            editingCommentId: null,
            editedCommentMessage: "",
            showDeleteConfirm: false,
            deleteCommentId: null,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            usersMap: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            comments: (state) => state.comments.comments,
        }),
    },
    watch: {
        comments: {
            handler(newComments) {
                console.log("Comments changed:", newComments);
            },
            deep: true,
        },
    },
    methods: {
        ...mapActions({
            fetchCommentsAction: "comments/fetchComments",
            deleteCommentAction: "comments/deleteComment",
            updateCommentAction: "comments/updateComment",
        }),
        async fetchComments() {
            try {
                await this.fetchCommentsAction({
                    type: "task",
                    id: this.taskId,
                });
            } catch (error) {
                console.error("Error fetching comments:", error);
            }
        },
        async deleteComment(commentId) {
            try {
                await this.deleteCommentAction({
                    id: commentId,
                });
                this.showDeleteConfirm = false;
                this.deleteCommentId = null;
            } catch (error) {
                console.error("Error deleting comment:", error);
            }
        },
        confirmDeleteComment(commentId) {
            this.showDeleteConfirm = true;
            this.deleteCommentId = commentId;
        },
        cancelDelete() {
            this.showDeleteConfirm = false;
            this.deleteCommentId = null;
        },
        editComment(comment) {
            this.editingCommentId = comment._id;
            this.editedCommentMessage = comment.message;
        },
        async saveComment(commentId) {
            try {
                await this.updateCommentAction({
                    id: commentId,
                    newMessage: this.editedCommentMessage,
                });
                this.editingCommentId = null;
                this.editedCommentMessage = "";
                this.fetchComments();
            } catch (error) {
                console.error("Error updating comment:", error);
            }
        },
        cancelEdit() {
            this.editingCommentId = null;
            this.editedCommentMessage = "";
        },
        formatDate(date) {
            return moment(date).format("DD MMM YYYY, HH:mm");
        },
    },
    created() {
        this.fetchComments();
    },
};
</script>

<style lang="scss" scoped>
#ListComment {
    .edited-label {
        display: inline-block;
        background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
        color: #555;
        padding: 3px 8px;
        margin-left: 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        letter-spacing: 0.5px;
    }

    .comment-item {
        background-color: #ffffff;
        padding: 22px;
        border-radius: 12px;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);
        margin-bottom: 24px;
        transition: all 0.3s ease;
        max-width: 600px;
        border: 1px solid rgba(0, 0, 0, 0.04);
        position: relative;
        overflow: hidden;

        &:hover {
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
            transform: translateY(-3px);
        }
        
        &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            width: 4px;
            background: linear-gradient(to bottom, #ec4b1d, #ff8a65);
        }

        .comment-header {
            display: flex;
            align-items: center;
            gap: 15px;
            margin-bottom: 18px;
            position: relative;

            .comment-username {
                font-weight: 600;
                font-size: 16px;
                color: #333;
                display: flex;
                align-items: center;
                flex-wrap: wrap;
            }

            .comment-date {
                font-size: 14px;
                color: #777;
                margin-left: auto;
                background: #f7f7f7;
                padding: 4px 10px;
                border-radius: 20px;
                font-weight: 500;
            }
        }

        .comment-body {
            font-size: 15px;
            color: #444;
            line-height: 1.6;
            padding-left: 50px; // İndent to align with avatar

            textarea {
                width: 100%;
                height: 120px;
                padding: 15px;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                margin-bottom: 15px;
                font-size: 14px;
                color: #333;
                resize: vertical;
                transition: border-color 0.3s, box-shadow 0.3s;
                
                &:focus {
                    border-color: #ec4b1d;
                    box-shadow: 0 0 0 3px rgba(236, 75, 29, 0.2);
                    outline: none;
                }
            }

            button {
                padding: 10px 18px;
                border: none;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

                &:first-of-type {
                    background: linear-gradient(135deg, #f44336, #e53935);
                    color: white;
                    margin-right: 10px;

                    &:hover {
                        background: linear-gradient(135deg, #e53935, #d32f2f);
                        box-shadow: 0 6px 10px rgba(244, 67, 54, 0.3);
                        transform: translateY(-2px);
                    }
                    
                    &:active {
                        transform: translateY(1px);
                    }
                }

                &:last-of-type {
                    background: linear-gradient(135deg, #4CAF50, #43A047);
                    color: white;

                    &:hover {
                        background: linear-gradient(135deg, #43A047, #388E3C);
                        box-shadow: 0 6px 10px rgba(76, 175, 80, 0.3);
                        transform: translateY(-2px);
                    }
                    
                    &:active {
                        transform: translateY(1px);
                    }
                }
            }
        }

        .comment-actions {
            margin-top: 16px;
            display: flex;
            gap: 15px;

            a {
                color: #ec4b1d;
                cursor: pointer;
                text-decoration: none;
                font-weight: 500;
                font-size: 14px;
                transition: all 0.3s ease;
                display: flex;
                align-items: center;
                
                &:hover {
                    color: #d43f1d;
                    transform: translateY(-1px);
                }
                
                &:active {
                    transform: translateY(1px);
                }
            }
        }
    }

    .delete-confirm-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(3px);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    .delete-confirm-dialog {
        background: #fff;
        padding: 30px;
        border-radius: 12px;
        box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
        text-align: center;
        width: 90%;
        max-width: 400px;
        animation: scaleIn 0.3s ease;
        
        @keyframes scaleIn {
            from { transform: scale(0.9); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
        }

        .confirm-content {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 25px;
            text-align: left;

            .alert-icon {
                width: 32px;
                height: 32px;
                color: #f44336;
                flex-shrink: 0;
            }

            p {
                font-size: 16px;
                color: #333;
                margin: 0;
                line-height: 1.5;
                font-weight: 500;
            }
        }

        button {
            padding: 12px 24px;
            border: none;
            border-radius: 8px;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            margin: 0 8px;
            min-width: 100px;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

            &:first-of-type {
                background: linear-gradient(135deg, #4CAF50, #43A047);
                color: white;

                &:hover {
                    background: linear-gradient(135deg, #43A047, #388E3C);
                    box-shadow: 0 6px 12px rgba(76, 175, 80, 0.3);
                    transform: translateY(-2px);
                }
                
                &:active {
                    transform: translateY(1px);
                }
            }

            &:last-of-type {
                background: linear-gradient(135deg, #f44336, #e53935);
                color: white;

                &:hover {
                    background: linear-gradient(135deg, #e53935, #d32f2f);
                    box-shadow: 0 6px 12px rgba(244, 67, 54, 0.3);
                    transform: translateY(-2px);
                }
                
                &:active {
                    transform: translateY(1px);
                }
            }
        }
    }
}
</style>
